import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import { updateGeneralSettings } from 'services/transparency-config-service';
import { DotsLoader } from 'Components/DotsLoader';
import { Dialog, Checkbox } from 'Components/ui';
import styled from 'styled-components';

const Card = styled.div`
  max-width: 680px;
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`

const Title = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 4px;
`

const Subtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0 0 20px;
  line-height: 1.6;
`

const Divider = styled.div`
  height: 1px;
  background: var(--vo-cloud);
  margin-bottom: 20px;
`

const FieldGroup = styled.div`
  margin-bottom: 20px;
`

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
`

const CheckboxLabel = styled.div`
  display: flex;
  flex-direction: column;
`

const LabelTitle = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-weight: 600;
  font-size: 14px;
  color: var(--vo-ink);
  margin: 0 0 3px;
`

const LabelCaption = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  line-height: 1.5;
`

const IndentGroup = styled.div`
  margin-left: 30px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
  opacity: ${({ $enabled }) => ($enabled ? 1 : 0.5)};
`

const Alert = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  background-color: var(--vo-warning-10);
  color: var(--vo-warning);
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  margin-bottom: 20px;
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`

const SaveButton = styled.button`
  height: 44px;
  background: var(--color-brand);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:hover { background: var(--color-brand-strong); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

export const GeneralSettings = ({ config, onUpdate, companyKey }) => {
  const { t } = useTranslation(['transparency', 'common']);
  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      transparencyEnabled: config.transparencyEnabled || false,
      publicAccessEnabled: config.publicAccessEnabled || false,
      protectedAccessEnabled: config.protectedAccessEnabled || false,
      showTrendPublicly: config.showTrendPublicly || false,
    },
  });

  const transparencyEnabled = watch('transparencyEnabled');

  const mutation = useMutation(
    (data) => updateGeneralSettings(companyKey, data),
    {
      onSuccess: () => {
        Dialog.alert({
          title: t('transparency:generalSettings.notifications.success'),
          message: t('transparency:generalSettings.notifications.successText'),
          icon: 'success',
        });
        onUpdate();
      },
      onError: (error) => {
        Dialog.alert({
          title: t('transparency:generalSettings.notifications.error'),
          message: error.response?.data?.message || error.message,
          icon: 'error',
        });
      },
    }
  );

  const onSubmit = (data) => {
    mutation.mutate(data);
  };

  return (
    <Card>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Title>{t('transparency:generalSettings.title')}</Title>
        <Subtitle>{t('transparency:generalSettings.subtitle')}</Subtitle>

        <Divider />

        <FieldGroup>
          <Controller
            control={control}
            name="transparencyEnabled"
            render={({ field: { onChange, value } }) => (
              <CheckboxWrapper>
                <Checkbox checked={value} onChange={onChange} />
                <CheckboxLabel>
                  <LabelTitle>{t('transparency:generalSettings.enableTransparency.label')}</LabelTitle>
                  <LabelCaption>{t('transparency:generalSettings.enableTransparency.caption')}</LabelCaption>
                </CheckboxLabel>
              </CheckboxWrapper>
            )}
          />
        </FieldGroup>

        {!transparencyEnabled && (
          <Alert>{t('transparency:generalSettings.disabledWarning')}</Alert>
        )}

        <IndentGroup $enabled={transparencyEnabled}>
          <Controller
            control={control}
            name="publicAccessEnabled"
            render={({ field: { onChange, value } }) => (
              <CheckboxWrapper $disabled={!transparencyEnabled}>
                <Checkbox checked={value} onChange={onChange} disabled={!transparencyEnabled} />
                <CheckboxLabel>
                  <LabelTitle>{t('transparency:generalSettings.enablePublic.label')}</LabelTitle>
                  <LabelCaption>{t('transparency:generalSettings.enablePublic.caption')}</LabelCaption>
                </CheckboxLabel>
              </CheckboxWrapper>
            )}
          />
          <Controller
            control={control}
            name="protectedAccessEnabled"
            render={({ field: { onChange, value } }) => (
              <CheckboxWrapper $disabled={!transparencyEnabled}>
                <Checkbox checked={value} onChange={onChange} disabled={!transparencyEnabled} />
                <CheckboxLabel>
                  <LabelTitle>{t('transparency:generalSettings.enableProtected.label')}</LabelTitle>
                  <LabelCaption>{t('transparency:generalSettings.enableProtected.caption')}</LabelCaption>
                </CheckboxLabel>
              </CheckboxWrapper>
            )}
          />
        </IndentGroup>

        <Divider />

        <FieldGroup style={{ marginBottom: 24 }}>
          <Controller
            control={control}
            name="showTrendPublicly"
            render={({ field: { onChange, value } }) => (
              <CheckboxWrapper $disabled={!transparencyEnabled}>
                <Checkbox checked={value} onChange={onChange} disabled={!transparencyEnabled} />
                <CheckboxLabel>
                  <LabelTitle>{t('transparency:generalSettings.showTrend.label')}</LabelTitle>
                  <LabelCaption>{t('transparency:generalSettings.showTrend.caption')}</LabelCaption>
                </CheckboxLabel>
              </CheckboxWrapper>
            )}
          />
        </FieldGroup>

        <Divider />

        <Actions>
          <SaveButton type="submit" disabled={mutation.isLoading}>
            {mutation.isLoading ? <DotsLoader /> : t('transparency:generalSettings.actions.save')}
          </SaveButton>
        </Actions>
      </form>
    </Card>
  );
};
